import type { CampaignEngagementItem } from "../../../api/campaigns";
import { ENGAGEMENT_STATE_COLOR } from "../../../api/campaigns";
import { useIsMobile } from "../../../lib/useMediaQuery";
import { pctLeft, pctWidth } from "./gantt-math";

interface Props {
  engagement: CampaignEngagementItem;
  windowStart: Date;
  spanMs: number;
  poolSize: number;
  onSelect: (sortOrder: number) => void;
}

export function GanttBar({ engagement: e, windowStart, spanMs, poolSize, onSelect }: Props) {
  const isMobile = useIsMobile();
  const s = e.scheduled_start_at ? new Date(e.scheduled_start_at) : windowStart;
  const end = e.scheduled_end_at ? new Date(e.scheduled_end_at) : windowStart;
  const left = pctLeft(s, windowStart, spanMs);
  const width = Math.max(pctWidth(s, end, windowStart, spanMs), 1.5); // min visible width
  const minWidthPx = isMobile ? 28 : undefined; // guarantee a tappable bar on phones
  const color = ENGAGEMENT_STATE_COLOR[e.state] ?? "var(--text-muted)";
  const done = e.targets_completed + e.targets_failed;
  const fillPct = poolSize > 0 ? Math.min(100, (done / poolSize) * 100) : 0;
  const running = e.state === "running";

  return (
    <div
      onClick={() => onSelect(e.sort_order)}
      title={`${e.engagement_display_name}\n${e.scheduled_start_at ?? "?"} → ${e.scheduled_end_at ?? "?"}\n${done}/${poolSize}`}
      style={{
        position: "absolute",
        left: `${left}%`,
        width: `${width}%`,
        minWidth: minWidthPx,
        top: 8,
        bottom: 8,
        background: "var(--bg-card)",
        border: `1.5px solid ${color}`,
        borderRadius: "var(--radius-input)",
        cursor: "pointer",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        padding: "0 8px",
        gap: 6,
        fontSize: 11,
        fontFamily: "var(--font-sans)",
        color: "var(--text-primary)",
        whiteSpace: "nowrap",
        zIndex: 2,
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
      }}
      onMouseEnter={(ev) => {
        (ev.currentTarget as HTMLDivElement).style.transform = "translateY(-1px)";
        (ev.currentTarget as HTMLDivElement).style.boxShadow = "0 4px 14px -6px rgba(15,23,42,0.22)";
        (ev.currentTarget as HTMLDivElement).style.zIndex = "5";
      }}
      onMouseLeave={(ev) => {
        (ev.currentTarget as HTMLDivElement).style.transform = "";
        (ev.currentTarget as HTMLDivElement).style.boxShadow = "";
        (ev.currentTarget as HTMLDivElement).style.zIndex = "2";
      }}
    >
      {/* progress fill */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: `${fillPct}%`,
          background: color,
          opacity: 0.18,
          pointerEvents: "none",
        }}
      />
      {/* running live-pulse dot — uses existing live-pulse keyframe from benthic-theme.css */}
      {running && (
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: color,
            flexShrink: 0,
            animation: "live-pulse 1.4s infinite",
            zIndex: 1,
          }}
        />
      )}
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          zIndex: 1,
          fontWeight: 500,
        }}
      >
        {e.engagement_display_name}
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          padding: "1px 5px",
          background: "rgba(15,23,42,0.06)",
          color: "var(--text-secondary)",
          flexShrink: 0,
          zIndex: 1,
        }}
      >
        {e.state}
      </span>
    </div>
  );
}
